import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

const hotelService = {
  // Test API connection
  async testConnection() {
    try {
      const response = await api.get('/health')
      return response.data
    } catch (error) {
      console.error('API connection test failed:', error)
      throw error
    }
  },

  // Fetch missing prices for hotels
  async fetchMissingPrices(hotels, searchCriteria) {
    try {
      const response = await api.post('/hotels/missing-prices', {
        hotels,
        searchCriteria
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch missing prices:', error)
      throw error
    }
  },
  // Search hotels by coordinates
  async searchHotelsByCoordinates(searchCriteria) {
    try {
      const response = await api.post('/hotels/availabilityByCoordinates', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error searching hotels by coordinates:', error)
      throw error
    }
  },

  // Search hotels (general search)
  async searchHotels(searchCriteria) {
    try {
      const response = await api.post('/hotels/search', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error searching hotels:', error)
      throw error
    }
  },

  // Search hotels by hotel code (specific hotel search)
  async searchHotelsByHotelCode(searchCriteria) {
    try {
      const response = await api.post('/hotels/availabilityByHotelCode', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error searching hotel by code:', error)
      throw error
    }
  },

  // Get hotel availability
  async getHotelAvailability(searchCriteria) {
    try {
      const response = await api.post('/hotels/availability', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel availability:', error)
      throw error
    }
  },

  // Get hotel availability by hotel code
  async getHotelAvailabilityByCode(searchCriteria) {
    try {
      const response = await api.post('/hotels/availabilityByHotelCode', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel availability by code:', error)
      throw error
    }
  },

  // Get hotel availability by address
  async getHotelAvailabilityByAddress(searchCriteria) {
    try {
      const response = await api.post('/hotels/availabilityAddress', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel availability by address:', error)
      throw error
    }
  },

  // Geo autocomplete for locations with enhanced category support
  async getGeoAutocomplete(query, options = {}) {
    try {
      // Support multiple categories for comprehensive results
      const defaultCategories = options.categories || ['CITY', 'AIR', 'POI'];
      const categoryParam = Array.isArray(defaultCategories) 
        ? defaultCategories.join(',') 
        : (options.category || 'CITY');
      
      const params = {
        query,
        category: categoryParam,
        limit: options.limit || 10,
        clientId: options.clientId || 'devstudio'
      }
      
      const response = await api.get('/hotels/autocomplete', { params })
      return response.data
    } catch (error) {
      console.error('Error getting geo autocomplete:', error)
      throw error
    }
  },

  // Hotel name autocomplete
  async getHotelNameAutocomplete(query) {
    try {
      const response = await api.get('/hotels/hotelnameAutoComplete', {
        params: { query }
      })
      return response.data
    } catch (error) {
      console.error('Error getting hotel name autocomplete:', error)
      throw error
    }
  },

  // Get hotel details by reference rate
  async getHotelDetailsByRefRateRef(searchCriteria) {
    try {
      const response = await api.post('/hotels/detailsByRefRateRef', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel details by ref rate:', error)
      throw error
    }
  },

  // Get hotel details by rate key
  async getHotelDetailsByRateKey(searchCriteria) {
    try {
      const response = await api.post('/hotels/detailsByRatekey', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel details by rate key:', error)
      throw error
    }
  },

  // Get hotel lead rate
  async getHotelLeadRate(searchCriteria) {
    try {
      const response = await api.post('/hotels/hotelleadrate', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel lead rate:', error)
      throw error
    }
  },

  // Get hotel rate info
  async getHotelRateInfo(searchCriteria) {
    try {
      const response = await api.post('/hotels/hotelrateinfo', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting hotel rate info:', error)
      throw error
    }
  },

  // Get aggregated hotel details
  async getAggregatedHotelDetails(searchCriteria) {
    try {
      const response = await api.post('/hotels/aggregatedDetails', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error getting aggregated hotel details:', error)
      throw error
    }
  },

  // Check hotel price
  async checkHotelPrice(searchCriteria) {
    try {
      const response = await api.post('/hotels/price-check', searchCriteria)
      return response.data
    } catch (error) {
      console.error('Error checking hotel price:', error)
      throw error
    }
  },

  // Get hotel media/images
  async getHotelMedia(hotelId) {
    try {
      const response = await api.get(`/hotels/${hotelId}/media`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel media:', error)
      throw error
    }
  },

  // Get hotel content
  async getHotelContent(hotelCode) {
    try {
      const response = await api.get(`/hotels/hotelcontent/${hotelCode}`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel content:', error)
      throw error
    }
  },

  // Get hotel descriptive info
  async getHotelDescriptiveInfo(hotelCode) {
    try {
      const response = await api.get(`/hotels/hoteldescriptiveinfo/${hotelCode}`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel descriptive info:', error)
      throw error
    }
  },

  // Get comprehensive hotel details
  async getHotelFullDetails(hotelCode) {
    try {
      const response = await api.get(`/hotels/details/${hotelCode}`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel full details:', error)
      throw error
    }
  },

  // ========================================
  // ENHANCED HOTEL DETAILS PAGE METHODS
  // ========================================

  // Get hotel details optimized for hotel details page
  async getHotelDetailsForHotelDetailsPage(hotelCode, queryParams = '') {
    try {
      const url = `/hotels/details-for-hotel-details-page/${hotelCode}${queryParams ? `?${queryParams}` : ''}`
      const response = await api.get(url)
      return response.data
    } catch (error) {
      console.error('Error getting hotel details for hotel details page:', error)
      throw error
    }
  },

  // Get hotel details with proper name extraction for the hotel details page
  async getHotelDetailsForPage(hotelCode) {
    try {
      const response = await api.get(`/hotels/details-for-page/${hotelCode}`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel details for page:', error)
      throw error
    }
  },

  // Get available rooms for hotel details page
  async getAvailableRoomsForHotelDetails(hotelCode, queryParams = '') {
    try {
      const url = `/hotels/available-rooms-for-hotel-details/${hotelCode}${queryParams ? `?${queryParams}` : ''}`
      const response = await api.get(url)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Get hotel images optimized for details page
  async getHotelImagesForDetailsPage(hotelCode) {
    try {
      const response = await api.get(`/hotels/images-for-details-page/${hotelCode}`)
      return response.data
    } catch (error) {
      console.error('Error getting hotel images for details page:', error)
      throw error
    }
  },

  // Price check for booking
  async priceCheckForBooking(priceCheckData) {
    try {
      const response = await api.post('/hotels/price-check-for-booking', priceCheckData)
      return response.data
    } catch (error) {
      console.error('Error checking price for booking:', error)
      throw error
    }
  },

  // ========================================
  // UTILITY METHODS FOR HOTEL DETAILS
  // ========================================

  // Format currency value
  formatCurrency(amount, currency = 'USD') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(amount) || 0)
    } catch (error) {
      return `${currency} ${parseFloat(amount || 0).toFixed(2)}`
    }
  },

  // Calculate nights between dates
  calculateNights(checkInDate, checkOutDate) {
    try {
      const checkIn = new Date(checkInDate)
      const checkOut = new Date(checkOutDate)
      const timeDiff = checkOut.getTime() - checkIn.getTime()
      return Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
    } catch (error) {
      return 1
    }
  },

  // Format date for display
  formatDate(dateString, options = {}) {
    try {
      const date = new Date(dateString)
      const defaultOptions = {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }
      return date.toLocaleDateString('en-US', { ...defaultOptions, ...options })
    } catch (error) {
      return dateString
    }
  },

  // Validate search criteria
  validateSearchCriteria(criteria) {
    const errors = []
    
    if (!criteria.checkInDate) {
      errors.push('Check-in date is required')
    }
    
    if (!criteria.checkOutDate) {
      errors.push('Check-out date is required')
    }
    
    if (criteria.checkInDate && criteria.checkOutDate) {
      // Parse dates without timezone issues
      const [checkInYear, checkInMonth, checkInDay] = criteria.checkInDate.split('-').map(Number)
      const [checkOutYear, checkOutMonth, checkOutDay] = criteria.checkOutDate.split('-').map(Number)
      
      const checkIn = new Date(checkInYear, checkInMonth - 1, checkInDay)
      const checkOut = new Date(checkOutYear, checkOutMonth - 1, checkOutDay)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      if (checkIn < today) {
        errors.push('Check-in date cannot be in the past')
      }
      
      if (checkOut <= checkIn) {
        errors.push('Check-out date must be after check-in date')
      }
    }
    
    if (criteria.adults && (criteria.adults < 1 || criteria.adults > 10)) {
      errors.push('Number of adults must be between 1 and 10')
    }
    
    if (criteria.children && (criteria.children < 0 || criteria.children > 10)) {
      errors.push('Number of children must be between 0 and 10')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  },

  // Extract rate plan features
  extractRatePlanFeatures(ratePlan) {
    const features = []
    
    try {
      // Check for free wifi
      if (ratePlan.inclusions?.includes('WiFi') || ratePlan.amenities?.includes('Free WiFi')) {
        features.push({ icon: '📶', text: 'Free WiFi' })
      }
      
      // Check for breakfast
      if (ratePlan.inclusions?.includes('Breakfast') || ratePlan.mealPlan?.includes('Breakfast')) {
        features.push({ icon: '🍳', text: 'Breakfast Included' })
      }
      
      // Check for cancellation
      if (ratePlan.cancellationPolicy?.includes('Free')) {
        features.push({ icon: '✅', text: 'Free Cancellation' })
      }
      
      // Check for refundable
      if (ratePlan.refundable) {
        features.push({ icon: '💰', text: 'Refundable' })
      }
      
      return features
    } catch (error) {
      return features
    }
  },

  // Process room data for display
  processRoomData(roomsData) {
    try {
      if (!roomsData?.rooms) return []
      
      return roomsData.rooms.map(room => ({
        ...room,
        processedRatePlans: room.RatePlans?.RatePlan ? (
          Array.isArray(room.RatePlans.RatePlan) 
            ? room.RatePlans.RatePlan 
            : [room.RatePlans.RatePlan]
        ) : [],
        roomTypeName: this.getRoomTypeName(room),
        bedInfo: this.extractBedInfo(room),
        amenities: this.extractRoomAmenities(room)
      }))
    } catch (error) {
      console.error('Error processing room data:', error)
      return []
    }
  },

  // Get room type name
  getRoomTypeName(room) {
    return room.RoomType || room.RoomDescription?.Name || room.roomTypeName || 'Standard Room'
  },

  // Extract bed information
  extractBedInfo(room) {
    try {
      if (room.bedTypes && room.bedTypes.length > 0) {
        return room.bedTypes.map(bed => `${bed.count || 1} ${bed.description || 'Bed'}`).join(', ')
      }
      
      if (room.BedTypeOptions?.BedTypes) {
        // Process Sabre bed type structure
        return 'Standard Bed Configuration'
      }
      
      return 'Standard Bed'
    } catch (error) {
      return 'Standard Bed'
    }
  },

  // Extract room amenities
  extractRoomAmenities(room) {
    try {
      const amenities = []
      
      if (room.Amenities?.Amenity) {
        const amenityList = Array.isArray(room.Amenities.Amenity) 
          ? room.Amenities.Amenity 
          : [room.Amenities.Amenity]
        
        amenityList.forEach(amenity => {
          if (amenity.Description) {
            amenities.push({
              description: amenity.Description,
              complimentary: amenity.ComplimentaryInd === true
            })
          }
        })
      }
      
      return amenities
    } catch (error) {
      return []
    }
  },

  // Showcase hotels from the Booking Platform catalog. Does not call Sabre availability.
  async getCatalogHotels(limit = 18) {
    try {
      const response = await api.get('/hotels/catalog', { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('Error getting catalog hotels:', error);
      throw error;
    }
  },

  // Get popular destinations list (fast, no hotel data)
  async getPopularDestinationsList() {
    try {
      const cacheKey = 'popular_destinations_list';
      
      // Check cache first
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const cacheAge = Date.now() - parsed.timestamp;
        // Cache valid for 24 hours (86400000 ms)
        if (cacheAge < 86400000) {
          return parsed.data;
        }
      }

      // Fetch from API
      const response = await api.get('/hotels/popular-destinations');
      
      const data = response.data;
      
      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      
      return data;
    } catch (error) {
      console.error('Error getting popular destinations list:', error)
      throw error
    }
  },

  // Get popular destination hotels with caching
  async getPopularDestinationHotels(destination, searchCriteria) {
    try {
      const cacheKey = `popular_hotels_${destination.name}_${destination.country}_${searchCriteria.checkInDate}_${searchCriteria.checkOutDate}_${searchCriteria.adults}_${searchCriteria.children}`;
      
      // Check cache first
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const cacheAge = Date.now() - parsed.timestamp;
        // Cache valid for 1 hour (3600000 ms)
        if (cacheAge < 3600000) {
          return parsed.data;
        }
      }

      // Fetch from API using new endpoint
      const response = await api.post('/hotels/popular-destinations/hotels', {
        destination,
        searchCriteria
      });
      
      const data = response.data;
      
      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      
      return data;
    } catch (error) {
      console.error('Error getting popular destination hotels:', error)
      throw error
    }
  },

  // Clear popular destination cache
  clearPopularDestinationCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('popular_hotels_')) {
        localStorage.removeItem(key);
      }
    });
  },


  async previewEmissions(emissionsData) {
    try {
      const response = await api.post(`hotels/preview-emissions`, emissionsData);
      return response.data;
    } catch (error) {
      console.error('Error calculating emissions:', error);
      throw error;
    }
  }
}

export default hotelService

// Export utility functions for use in components
export const {
  formatCurrency,
  calculateNights,
  formatDate,
  validateSearchCriteria,
  extractRatePlanFeatures,
  processRoomData
} = hotelService
